import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  ChecklistItem,
  ChecklistStatus
} from 'src/interfaces/ChecklistTypes';
import { AdminUser } from 'src/interfaces/TicketTypes';
import ChecklistStepRow from './ChecklistStepRow';

interface Props {
  installationId: number;
}

type ToastState = {
  open: boolean;
  severity: 'success' | 'error';
  message: string;
};

function InstallationChecklistTab({ installationId }: Props) {
  const intl = useIntl();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<ToastState>({
    open: false,
    severity: 'success',
    message: ''
  });

  const fetchItems = useCallback(() => {
    setLoading(true);
    axiosConfig
      .get('/GetChecklistForInstallation', { params: { installationId } })
      .then((res) => {
        setItems(Array.isArray(res.data) ? res.data : []);
        setError('');
      })
      .catch(() => setError('Failed to load checklist.'))
      .finally(() => setLoading(false));
  }, [installationId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    axiosConfig
      .get('/GetAdminUsers')
      .then((res) => {
        if (Array.isArray(res.data)) setAdminUsers(res.data);
      })
      .catch(() => setAdminUsers([]));
  }, []);

  const progress = useMemo(() => {
    const total = items.length;
    const done = items.filter(
      (i) => i.status === ChecklistStatus.Done
    ).length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, done, percent };
  }, [items]);

  const handleUpdate = useCallback(
    async (
      id: number,
      patch: Partial<{
        status: number;
        comments: string;
        assigneeId: number | null;
        doneAt: string | null;
        subtasks: string;
      }>
    ) => {
      const params: Record<string, unknown> = { checklistItemId: id };
      if (patch.status !== undefined) params.status = patch.status;
      if (patch.comments !== undefined) params.comments = patch.comments;
      if (patch.subtasks !== undefined) params.subtasks = patch.subtasks;

      if ('assigneeId' in patch) {
        if (patch.assigneeId === null) {
          params.clearAssignee = true;
        } else if (typeof patch.assigneeId === 'number') {
          params.assigneeId = patch.assigneeId;
        }
      }

      if ('doneAt' in patch) {
        params.doneAt = patch.doneAt ?? '';
      }

      try {
        const res = await axiosConfig.put('/UpdateChecklistItem', null, {
          params
        });
        const updated = res.data as ChecklistItem;
        setItems((prev) =>
          prev.map((it) => (it.id === id ? updated : it))
        );
        return true;
      } catch {
        setToast({
          open: true,
          severity: 'error',
          message: intl.formatMessage({
            id: 'checklistSaveFailed',
            defaultMessage: 'Failed to save change'
          })
        });
        return false;
      }
    },
    [intl]
  );

  const handleNotify = useCallback(
    async (id: number) => {
      const item = items.find((i) => i.id === id);
      if (!item || !item.assigneeId) return false;
      const assignee = adminUsers.find((u) => u.id === item.assigneeId);
      const assigneeName = assignee?.name ?? '';
      try {
        await axiosConfig.post('/NotifyChecklistAssignee', null, {
          params: { checklistItemId: id }
        });
        setToast({
          open: true,
          severity: 'success',
          message: intl.formatMessage(
            {
              id: 'checklistEmailSent',
              defaultMessage: 'Email sent to {name}'
            },
            { name: assigneeName }
          )
        });
        return true;
      } catch {
        setToast({
          open: true,
          severity: 'error',
          message: intl.formatMessage({
            id: 'checklistEmailFailed',
            defaultMessage: 'Failed to send email — try again'
          })
        });
        return false;
      }
    },
    [items, adminUsers, intl]
  );

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h4">
            <FormattedMessage
              id="checklistTitle"
              defaultMessage="Steps to Bring Installation to Monitor"
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="checklistProgress"
              defaultMessage="Progress: {done}/{total} ({percent}%)"
              values={progress}
            />
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress.percent}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>
                <FormattedMessage id="checklistStep" defaultMessage="Step" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="checklistStatus" defaultMessage="Status" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="checklistAssignee" defaultMessage="Assignee" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="checklistDateDone" defaultMessage="Date Done" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="checklistComments" defaultMessage="Comments" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <ChecklistStepRow
                key={item.id}
                item={item}
                installationId={installationId}
                adminUsers={adminUsers}
                onUpdate={handleUpdate}
                onNotify={handleNotify}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InstallationChecklistTab;
